// Text boxes that rewrite what's typed (a SKU upper-cased, a pasted type prefix dropped; a currency upper-cased) lose
// their cursor: React puts the rewritten text back and the browser moves the cursor to the end, so after the first
// character everything typed in the middle lands at the end. useCaret() puts the cursor back where the typing left it,
// counted through the rewrite. Any box that changes its text in onChange uses it (SkuInput, the currency boxes).
import { type ChangeEvent, type Ref, useCallback, useLayoutEffect, useRef } from "react";

type Box = HTMLInputElement | HTMLTextAreaElement;

/** `outer`: the caller's own ref to the box (focus), kept working. */
export function useCaret<T extends Box = HTMLInputElement>(outer?: Ref<T>) {
  const box = useRef<T | null>(null);
  const want = useRef<number | null>(null);
  // After the rewritten text is on screen: the cursor where it belongs, if the box still has the focus.
  useLayoutEffect(() => {
    const el = box.current;
    const at = want.current;
    want.current = null;
    if (at !== null && el && document.activeElement === el) el.setSelectionRange(at, at);
  });
  const ref = useCallback(
    (node: T | null) => {
      box.current = node;
      if (typeof outer === "function") outer(node);
      else if (outer) outer.current = node;
    },
    [outer],
  );
  /** This change's text rewritten by `to`; the cursor stays after what was typed, wherever that is in the new text. */
  const rewrite = (e: ChangeEvent<T>, to: (text: string) => string) => {
    const raw = e.target.value;
    const next = to(raw);
    if (next !== raw) want.current = Math.min(next.length, to(raw.slice(0, e.target.selectionEnd ?? raw.length)).length);
    return next;
  };
  return { ref, rewrite };
}
